/**
 * Wire scope ⇄ store scope, and the honest answer to "which scopes did we
 * actually consult?".
 *
 * THE WIRE FORM IS NEVER THE RAW INTERNAL SCOPE. `personalScopeId()` is
 * `personal:<userId>`; handing that to an external client would leak the user
 * id for nothing. Over MCP a scope is `personal`, `personal/<workspaceId>`
 * (a user-defined document workspace, card 4.4 — documents only) or
 * `team:<teamId>`. The workspace form lives in docScopes.js; the shape check
 * is here, mirrored in Rust and pinned by tests/fixtures/mcpWireScopes.json.
 *
 * ── Why every list result carries scopesConsulted / scopesUnavailable ──
 *
 * `useWorkspaceBootstrap.js:134-143` loads teams in the background,
 * fire-and-forget, and calls `setTeams([])` on failure. `teamBoardScopes()`
 * (boardsStore) and `docTeamScopes()` (useAiExtensionActions) both read that
 * array and silently return a NARROWER result set. So an MCP request landing
 * in the first seconds after launch — or after a failed teams fetch — returns
 * only personal-scope data **with no error at all**, and an external agent
 * would reasonably conclude the user has no team documents.
 *
 * There is no "teams loaded" flag anywhere to distinguish "no teams" from
 * "not loaded yet", so this module does not pretend there is: when the user is
 * signed in and the array is empty, the team scopes are reported as
 * UNAVAILABLE rather than as absent. Over-reporting uncertainty is the safe
 * direction; the `instructions` text tells the model to say so rather than to
 * conclude the data does not exist.
 */
import { useTeamsStore } from '../store/teamsStore';
import { useAuthStore } from '../store/authStore';

export const PERSONAL = 'personal';
/** `personal/<docscope id>` — a user-defined document workspace (card 4.4). */
export const WORKSPACE_PREFIX = 'personal/';

const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

/** The docscope id of a `personal/<id>` wire scope, or null for anything else. */
export function workspaceIdOf(wireScope) {
  if (typeof wireScope !== 'string' || !wireScope.startsWith(WORKSPACE_PREFIX)) return null;
  const id = wireScope.slice(WORKSPACE_PREFIX.length);
  return ID_RE.test(id) ? id : null;
}

/**
 * `personal` | `personal/<id>` | `team:<id>` → the descriptor
 * documentsStore/boardsStore take. Plain `personal` stays BUCKET-level (every
 * workspace), so a read or write by id keeps finding a document after it moved
 * between workspaces; listings that mean "Personal only" use docScopes.js
 * `toListScope`.
 */
export function toDocScope(wireScope) {
  if (!wireScope || wireScope === PERSONAL) return { type: 'personal' };
  const workspace = workspaceIdOf(wireScope);
  if (workspace) return { type: 'personal', scopeId: workspace };
  const teamId = String(wireScope).slice('team:'.length);
  return { type: 'team', teamId };
}

/** The same shape validated in Rust (`mcp::resources::valid_scope`). */
export function isWireScope(wireScope) {
  if (wireScope === PERSONAL) return true;
  if (workspaceIdOf(wireScope)) return true;
  if (typeof wireScope !== 'string' || !wireScope.startsWith('team:')) return false;
  return ID_RE.test(wireScope.slice('team:'.length));
}

function teams() {
  try {
    return useTeamsStore.getState().teams || [];
  } catch {
    return [];
  }
}

function signedIn() {
  try {
    return !!useAuthStore.getState().isAuthenticated;
  } catch {
    return false;
  }
}

/**
 * Every scope the user can reach right now, as `{ scope, label }`.
 *
 * `label` is a team NAME, and `scopes_list` is `Surface::Any` — the OR of the
 * grantable reads (capability.rs) — so a user who granted only `memory: read`
 * still hands a client the names of every team they belong to. That is the
 * deliberate shape of `Surface::Any` and not a hole (nothing else about a team
 * is exposed, and a team's documents and boards stay behind their own grants),
 * but it was undescribed on the settings page until `mcp.scopesNote`. If a
 * label ever becomes more than a display name, this is the line to revisit.
 */
export function listScopes() {
  return [
    { scope: PERSONAL, label: 'Personal' },
    ...teams()
      .filter((t) => t?.id != null)
      .map((t) => ({ scope: `team:${t.id}`, label: t.name || `Team ${t.id}` })),
  ];
}

/**
 * The scopes a request should read, plus the ones it could not.
 *
 * `requested` null means "everything reachable". A single requested scope is
 * still reported, so a caller that asked for one team and got nothing can tell
 * the difference between an empty team and an unreachable one.
 */
export function resolveScopes(requested) {
  if (requested && requested !== PERSONAL) {
    const known = listScopes().some((s) => s.scope === requested);
    return {
      scopes: known ? [requested] : [],
      consulted: known ? [requested] : [],
      unavailable: known ? [] : [requested],
    };
  }
  if (requested === PERSONAL) {
    return { scopes: [PERSONAL], consulted: [PERSONAL], unavailable: [] };
  }
  const teamScopes = teams().filter((t) => t?.id != null).map((t) => `team:${t.id}`);
  const unavailable = signedIn() && teamScopes.length === 0 ? ['team:*'] : [];
  return {
    scopes: [PERSONAL, ...teamScopes],
    consulted: [PERSONAL, ...teamScopes],
    unavailable,
  };
}
